import { useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import * as React from 'react';
import { useAtom, useAtomValue } from 'jotai';
import { useTranslation } from 'next-i18next';

import { useWorkersService } from '@restworld/data-services';
import { Button } from '@restworld/ui-ds-base';

import {
  notificationModalStateAtom,
  notificationModalToggleAtom,
  notificationResearchIdAtom,
  notificationChangeAtom,
  userUnsubscribeReasonAtom,
  userUnsubscribeReasonFreeTextAtom,
  saveResearchErrorReturnAtom,
} from './save-research-tab.atoms';

const SavedResearchNotificationModalFooter = () => {
  const queryClient = useQueryClient();
  const workerServices = useWorkersService();
  const [loading, onLoading] = useState(false);
  const [loadingDelete, setLoadingDelete] = useState(false);

  const [, setNotificationModalToggle] = useAtom(notificationModalToggleAtom);
  const saveResearchId = useAtomValue(notificationResearchIdAtom);
  const [notificationModalState, setNotificationModalState] = useAtom(notificationModalStateAtom);
  const notificationChange = useAtomValue(notificationChangeAtom);
  const userUnsubscribeReason = useAtomValue(userUnsubscribeReasonAtom);
  const userUnsubscribeReasonFreeText = useAtomValue(userUnsubscribeReasonFreeTextAtom);
  const [, setSaveResearchErrorReturn] = useAtom(saveResearchErrorReturnAtom);
  const { t } = useTranslation();

  const handleSaveModification = React.useCallback(async () => {
    try {
      onLoading(true);
      if (notificationChange === 'unsubscribe') setNotificationModalState('unsubscribe_user_review');
      else {
        await workerServices.updateSavedResearch({
          id: saveResearchId,
          frequency: notificationChange,
          enabled: true,
        });

        setNotificationModalState('frequency_change_success');
        queryClient.invalidateQueries({
          queryKey: ['listSaveResearch'],
        });
      }
    } catch (error) {
      console.error(`Failed to update saved research ${saveResearchId}`, error);
    } finally {
      onLoading(false);
    }
  }, [notificationChange, queryClient, saveResearchId, setNotificationModalState, workerServices]);

  const handleUnsubscribeReasonChange = React.useCallback(async () => {
    if ((userUnsubscribeReason === 'other' && !userUnsubscribeReasonFreeText) || userUnsubscribeReason === '') {
      setSaveResearchErrorReturn(t('user-settings:enter_reason_for_unsubscribing'));
      return;
    }
    try {
      onLoading(true);
      await workerServices.updateSavedResearch({
        id: saveResearchId,
        enabled: false,
        ...(userUnsubscribeReason !== 'other'
          ? { unsubscribe_reason: userUnsubscribeReason }
          : userUnsubscribeReasonFreeText
          ? {
              unsubscribe_reason_free_text: userUnsubscribeReasonFreeText,
            }
          : {}),
      });
      queryClient.invalidateQueries({
        queryKey: ['listSaveResearch'],
      });
      setNotificationModalState('unsubscribe_review_success');
    } catch (error) {
      console.error(`Failed to update saved research ${saveResearchId}`, error);
    } finally {
      onLoading(false);
    }
  }, [
    t,
    queryClient,
    saveResearchId,
    setNotificationModalState,
    setSaveResearchErrorReturn,
    userUnsubscribeReason,
    userUnsubscribeReasonFreeText,
    workerServices,
  ]);

  const handleDeleteResearch = React.useCallback(async () => {
    try {
      setLoadingDelete(true);
      const data = (
        await workerServices.deleteSavedResearch({
          researchId: saveResearchId,
        })
      ).data;
      setNotificationModalState('delete_save_research_success');
      queryClient.invalidateQueries({
        queryKey: ['listSaveResearch'],
      });
      return data;
    } catch (error) {
      console.error(`Failed to delete saved research ${saveResearchId}`, error);
    } finally {
      setLoadingDelete(false);
    }
  }, [queryClient, saveResearchId, setNotificationModalState, workerServices]);

  const footerEl = useMemo(() => {
    switch (notificationModalState) {
      case 'unsubscribe_user_review':
        return (
          <div className="flex items-center justify-center w-full">
            <Button variant="primary" fullwidth loading={loading} onClick={handleUnsubscribeReasonChange}>
              {t('common:unsubscribe')}
            </Button>
          </div>
        );
      case 'frequency_change_success':
      case 'delete_save_research_success':
      case 'unsubscribe_review_success':
        return (
          <div className="flex items-center justify-center w-full">
            <Button
              variant="primary"
              fullwidth
              onClick={() => {
                setNotificationModalToggle(false);
              }}
            >
              {t('common:close')}
            </Button>
          </div>
        );
      default:
        return (
          <div className="flex flex-col gap-4 items-center justify-center w-full">
            <Button variant="primary" fullwidth loading={loading} onClick={handleSaveModification}>
              {t('common:save_changes')}
            </Button>
            <Button
              variant="tertiary"
              className="text-red-500 underline"
              fullwidth
              loading={loadingDelete}
              onClick={handleDeleteResearch}
            >
              {t('user-settings:delete_search')}
            </Button>
          </div>
        );
    }
  }, [
    t,
    handleDeleteResearch,
    handleSaveModification,
    handleUnsubscribeReasonChange,
    loading,
    loadingDelete,
    notificationModalState,
    setNotificationModalToggle,
  ]);
  return footerEl;
};
export default SavedResearchNotificationModalFooter;
